import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronUp, Loader2, RotateCcw, X } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import { cn } from "utils/cn";
import { aiRunFeatureLabel, aiRunFeaturePanelPath } from "utils/aiRunTransitions";
import { useActiveAiRuns } from "./activeAiRunsStore";
import { useBackgroundTasks } from "./useBackgroundTasks";

/** Compact size for the per-task Retry and Open actions in the list. */
const TASK_ACTION_CLASS = "h-6 shrink-0 gap-1 px-2 text-[11px] [&_svg]:h-3 [&_svg]:w-3";
const TASK_ICON_ACTION_CLASS = "h-6 w-6 [&_svg]:h-3 [&_svg]:w-3";

const stageLabel = (stage: string, uploadProgress?: number): string => {
  switch (stage) {
    case "uploading":
      return typeof uploadProgress === "number"
        ? `Uploading ${Math.round(uploadProgress * 100)}%`
        : "Uploading";
    case "parsing":
      return "Parsing";
    case "finalizing":
      return "Finalizing";
    default:
      return "Creating";
  }
};

/**
 * Small floating pill shown while background work is in flight: project
 * creations and AI runs. Expands into a list with per-item actions.
 */
export const BackgroundTasksIndicator: React.FC = () => {
  const { createProjectTasks, dismissCreateProjectTask, retryCreateProjectTask } =
    useBackgroundTasks();
  const activeAiRuns = useActiveAiRuns();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const runningTasks = createProjectTasks.filter((t) => t.status === "running");
  const failedTasks = createProjectTasks.filter((t) => t.status === "error");
  const aiEntries = Object.values(activeAiRuns);

  if (!runningTasks.length && !failedTasks.length && !aiEntries.length) {
    return null;
  }

  const summaryParts: string[] = [];
  if (runningTasks.length) {
    summaryParts.push(
      runningTasks.length === 1
        ? "1 project importing"
        : `${runningTasks.length} projects importing`
    );
  }
  if (aiEntries.length) {
    const runCount = aiEntries.reduce((sum, entry) => sum + entry.features.length, 0);
    summaryParts.push(runCount === 1 ? "1 AI run" : `${runCount} AI runs`);
  }
  if (failedTasks.length) {
    summaryParts.push(failedTasks.length === 1 ? "1 failed" : `${failedTasks.length} failed`);
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      {open && (
        <div className="w-80 overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-xs font-medium text-foreground">Background tasks</span>
            <Button
              type="button"
              color="gray"
              size="icon"
              aria-label="Collapse background tasks"
              className={TASK_ICON_ACTION_CLASS}
              onClick={() => setOpen(false)}
            >
              <X />
            </Button>
          </div>
          <div className="max-h-72 overflow-y-auto p-2">
            {runningTasks.map((task) => (
              <div key={task.id} className="flex items-center gap-2 rounded-lg px-2 py-2">
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
                <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                  {task.projectName}
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {stageLabel(task.stage, task.uploadProgress)}
                </span>
              </div>
            ))}
            {failedTasks.map((task) => (
              <div key={task.id} className="flex items-center gap-2 rounded-lg px-2 py-2">
                <span className="h-2 w-2 shrink-0 rounded-full bg-danger" />
                <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                  {task.projectName}
                </span>
                <Button
                  type="button"
                  color="gray"
                  size="xs"
                  className={TASK_ACTION_CLASS}
                  onClick={() => retryCreateProjectTask(task.id)}
                >
                  <RotateCcw />
                  Retry
                </Button>
                <Button
                  type="button"
                  color="gray"
                  size="icon"
                  aria-label={`Dismiss failed task ${task.projectName}`}
                  className={cn("shrink-0", TASK_ICON_ACTION_CLASS)}
                  onClick={() => dismissCreateProjectTask(task.id)}
                >
                  <X />
                </Button>
              </div>
            ))}
            {aiEntries.map((entry) => {
              const firstFeature = entry.features[0];
              const openPath =
                entry.workspaceId && firstFeature
                  ? `/workspaces/${entry.workspaceId}/projects/${entry.projectId}/${aiRunFeaturePanelPath(firstFeature)}`
                  : null;
              return (
                <div key={entry.projectId} className="flex items-center gap-2 rounded-lg px-2 py-2">
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
                  <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                    {entry.projectName ?? `Project ${entry.projectId}`}
                    <span className="ml-1 text-[11px] text-muted-foreground">
                      {entry.features.map(aiRunFeatureLabel).join(", ")}
                    </span>
                  </span>
                  {openPath && (
                    <Button
                      type="button"
                      color="gray"
                      size="xs"
                      className={TASK_ACTION_CLASS}
                      onClick={() => navigate(openPath)}
                    >
                      Open
                      <ArrowRight />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-2 shadow-md transition",
          "hover:border-primary/35 hover:shadow-lg",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        )}
      >
        {failedTasks.length ? (
          <span className="h-2 w-2 rounded-full bg-danger" />
        ) : (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        )}
        <span className="text-xs font-medium text-foreground">{summaryParts.join(" · ")}</span>
        <ChevronUp
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
    </div>
  );
};
